// 헤더: blur on scroll + 그룹 드롭다운 + 모바일 풀스크린 메뉴 + 언어 토글
import { useEffect, useRef, useState } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from './Button'
import { useTheme } from '../hooks/useTheme'
import { useT, type Lang } from '../lib/i18n'
import type { TranslationKey } from '../locales/ko'

type NavLeaf = { to: string; labelKey: TranslationKey; icon: any }
type NavGroup = { labelKey: TranslationKey; icon: any; items: NavLeaf[] }
type NavNode = NavLeaf | NavGroup
const isGroup = (n: NavNode): n is NavGroup => 'items' in n

const NAV: NavNode[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: 'gauge' },
  {
    labelKey: 'nav.group.operations', icon: 'industry',
    items: [
      { to: '/production-orders', labelKey: 'nav.production',    icon: 'clipboard-list' },
      { to: '/work-orders',       labelKey: 'nav.workOrders',    icon: 'industry' },
      { to: '/equipment-data',    labelKey: 'nav.equipmentData', icon: 'microchip' },
    ],
  },
  {
    labelKey: 'nav.group.quality', icon: 'magnifying-glass-chart',
    items: [
      { to: '/inspection', labelKey: 'nav.inspection', icon: 'magnifying-glass-chart' },
      { to: '/spc',        labelKey: 'nav.spc',        icon: 'chart-area' },
      { to: '/lots',       labelKey: 'nav.lots',       icon: 'sitemap' },
    ],
  },
  {
    labelKey: 'nav.group.equipment', icon: 'tower-observation',
    items: [
      { to: '/andon',            labelKey: 'nav.andon',           icon: 'tower-observation' },
      { to: '/equipment-status', labelKey: 'nav.equipmentStatus', icon: 'chart-line' },
      { to: '/maintenance',      labelKey: 'nav.maintenance',     icon: 'wrench' },
    ],
  },
  {
    labelKey: 'nav.group.planning', icon: 'calendar-days',
    items: [
      { to: '/schedule',    labelKey: 'nav.schedule',    icon: 'calendar-days' },
      { to: '/forecasting', labelKey: 'nav.forecasting', icon: 'robot' },
      { to: '/cost',        labelKey: 'nav.cost',        icon: 'money-bill-trend-up' },
    ],
  },
  {
    labelKey: 'nav.group.master', icon: 'list-check',
    items: [
      { to: '/boms',      labelKey: 'nav.boms',      icon: 'list-check' },
      { to: '/labor',     labelKey: 'nav.labor',     icon: 'users' },
      { to: '/inventory', labelKey: 'nav.inventory', icon: 'warehouse' },
    ],
  },
]

function NavGroupButton({
  group, t, currentPath,
}: {
  group: NavGroup
  t: (k: TranslationKey, vars?: Record<string, string>) => string
  currentPath: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const hoverTimerRef = useRef<number | null>(null)
  const active = group.items.some((it) => currentPath.startsWith(it.to))

  // pathname 변경 시(NavLink 선택) 자동 닫기
  useEffect(() => { setOpen(false) }, [currentPath])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus() }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // hover로도 열림(보조). 단 mouseLeave는 짧은 디바운스로 사용자가 click→이동 시 깜빡임 방지.
  const onEnter = () => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null }
    setOpen(true)
  }
  const onLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = window.setTimeout(() => setOpen(false), 180)
  }

  return (
    <div
      ref={ref}
      className="nav-group"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <button
        ref={btnRef}
        type="button"
        className="nav-group-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-current={active ? 'page' : undefined}
        onClick={(e) => {
          e.stopPropagation()
          if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null }
          setOpen((v) => !v)
        }}
      >
        <FontAwesomeIcon icon={group.icon} aria-hidden="true" />
        <span>{t(group.labelKey)}</span>
        <FontAwesomeIcon icon="chevron-down" aria-hidden="true" className="chev" />
      </button>
      <div className="nav-group-menu" role="menu" data-open={open}>
        {group.items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <FontAwesomeIcon icon={it.icon} aria-hidden="true" />
            <span>{t(it.labelKey)}</span>
          </NavLink>
        ))}
      </div>
    </div>
  )
}

export function Header({
  onLogout,
  isAuthenticated,
}: {
  onLogout: () => void
  isAuthenticated: boolean
}) {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const headerRef = useRef<HTMLElement | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const { theme, cycle } = useTheme()
  const { lang, setLang, t } = useT()
  const { pathname } = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!open) return
    closeBtnRef.current?.focus()
    document.body.style.overflow = 'hidden'
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', esc)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', esc)
    }
  }, [open])

  const nextLang: Lang = lang === 'ko' ? 'en' : 'ko'

  return (
    <header ref={headerRef} className={`site-header ${scrolled ? 'scrolled' : ''}`} role="banner">
      <Link to="/dashboard" className="brand" aria-label={t('brand.home')}>
        <span className="brand-mark" aria-hidden="true">
          <FontAwesomeIcon icon="industry" />
        </span>
        <span>
          {t('brand.name')} <span className="muted">{t('brand.suffix')}</span>
        </span>
      </Link>

      {isAuthenticated && (
        <nav className="primary" aria-label={t('nav.primary')} style={{ display: 'none' }} data-desktop-nav>
          {NAV.map((n) =>
            isGroup(n) ? (
              <NavGroupButton key={n.labelKey} group={n} t={t} currentPath={pathname} />
            ) : (
              <NavLink key={n.to} to={n.to}>
                <FontAwesomeIcon icon={n.icon} aria-hidden="true" /> <span>{t(n.labelKey)}</span>
              </NavLink>
            ),
          )}
        </nav>
      )}

      <div className="right">
        <Button
          variant="ghost"
          aria-label={t('common.lang.toggle', { lang: t(`lang.${lang}` as TranslationKey) })}
          onClick={() => setLang(nextLang)}
          magnetic={false}
          title={t(`lang.${nextLang}` as TranslationKey)}
        >
          <span aria-hidden="true" style={{ fontWeight: 700, letterSpacing: '0.04em' }}>
            {lang === 'ko' ? 'EN' : 'KO'}
          </span>
        </Button>

        <Button
          variant="ghost"
          aria-label={t('common.theme.toggle', { theme })}
          onClick={cycle}
          magnetic={false}
        >
          <FontAwesomeIcon
            icon={theme === 'dark' ? 'sun' : theme === 'light' ? 'moon' : 'sliders'}
            aria-hidden="true"
          />
        </Button>

        {isAuthenticated && (
          <NavLink
            to="/account"
            aria-label={t('nav.account')}
            title={t('nav.account')}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44, borderRadius: 'var(--radius-sm)',
              color: 'inherit', textDecoration: 'none',
            }}
          >
            <FontAwesomeIcon icon="user" aria-hidden="true" />
            <span className="sr-only">{t('nav.account')}</span>
          </NavLink>
        )}

        {isAuthenticated && (
          <Button
            variant="ghost"
            onClick={onLogout}
            aria-label={t('common.logout')}
            magnetic={false}
          >
            <FontAwesomeIcon icon="right-from-bracket" aria-hidden="true" />
            <span className="sr-only">{t('common.logout')}</span>
          </Button>
        )}

        {isAuthenticated && (
          <Button
            variant="ghost"
            aria-label={t('common.menu.open')}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen(true)}
            data-mobile-only
            magnetic={false}
          >
            <FontAwesomeIcon icon="bars" aria-hidden="true" />
          </Button>
        )}
      </div>

      <div
        id="mobile-menu"
        className="menu-overlay"
        data-open={isAuthenticated && open}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.full')}
        aria-hidden={!isAuthenticated || !open}
        onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
      >
        <nav aria-label={t('nav.allMenu')}>
          {isAuthenticated && NAV.map((n) => {
            if (!isGroup(n)) {
              return (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}>
                  <FontAwesomeIcon icon={n.icon} aria-hidden="true" /> {t(n.labelKey)}
                </NavLink>
              )
            }
            return (
              <div key={n.labelKey} className="mobile-nav-group">
                <div className="mobile-nav-group-label">
                  <FontAwesomeIcon icon={n.icon} aria-hidden="true" /> {t(n.labelKey)}
                </div>
                {n.items.map((it) => (
                  <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)}>
                    <FontAwesomeIcon icon={it.icon} aria-hidden="true" /> {t(it.labelKey)}
                  </NavLink>
                ))}
              </div>
            )
          })}
          {isAuthenticated && (
            <NavLink to="/account" onClick={() => setOpen(false)}>
              <FontAwesomeIcon icon="user" aria-hidden="true" /> {t('nav.account')}
            </NavLink>
          )}
        </nav>
        <Button
          ref={closeBtnRef}
          variant="ghost"
          aria-label={t('common.menu.close')}
          onClick={() => setOpen(false)}
          style={{ position: 'absolute', top: 16, right: 16 }}
          magnetic={false}
        >
          <FontAwesomeIcon icon="xmark" aria-hidden="true" />
        </Button>
      </div>

      <style>{`
        @media (min-width: 900px) {
          [data-desktop-nav] { display: flex !important; }
          [data-mobile-only] { display: none !important; }
        }
      `}</style>
    </header>
  )
}
